import { formatTopicTitle } from "../components/OverviewChart/chartPrimitives.js";
import {
  shareOfWeekPercent,
  type OvertimeWeek,
} from "../components/TopConceptsOvertimeChart/buildOvertimeData.js";
import { textCell } from "./csv-cells.js";

/** Column labels the chart translates and passes in — this helper must not hardcode copy. */
export interface OvertimeChartCsvLabels {
  week: string;
  weekStart: string;
  shareColumn: (concept: string) => string;
}

/** Unformatted number so spreadsheets can compute on it. Missing topics are 0, matching the chart. */
function numericCell(value: number): string {
  return String(value);
}

/**
 * Serialize the overtime chart's plotted weeks × top concepts as CSV.
 *
 * Each concept contributes two columns: its raw asset count (the Raw toggle) and
 * its share of that week's top-concept total (the Normalized toggle). Both encodings
 * are always written so the file does not depend on which toggle is active.
 */
export function buildOvertimeChartCsv(
  weeks: OvertimeWeek[],
  topTopics: string[],
  labels: OvertimeChartCsvLabels,
): string {
  const header = [
    labels.week,
    labels.weekStart,
    ...topTopics.flatMap((topic) => {
      const label = formatTopicTitle(topic);
      return [label, labels.shareColumn(label)];
    }),
  ].map(textCell);

  const rows = weeks.map((week) => {
    const weekTotal = topTopics.reduce((sum, topic) => sum + (week.values[topic] ?? 0), 0);
    return [
      textCell(week.label),
      textCell(week.weekStart),
      ...topTopics.flatMap((topic) => {
        const raw = week.values[topic] ?? 0;
        return [numericCell(raw), numericCell(shareOfWeekPercent(raw, weekTotal))];
      }),
    ];
  });

  return [header, ...rows].map((row) => row.join(",")).join("\n");
}

/** `overtime-top-concepts-2026-09-06.csv` — dated so repeat exports do not collide. */
export function overtimeChartCsvFilename(now: Date = new Date()): string {
  return `overtime-top-concepts-${now.toISOString().slice(0, 10)}.csv`;
}
